import { useEffect, useState } from 'react';
import { useAppState } from '../../app/state';
import { registerHandle } from '../../app/handles';
import { isConnectedDeployment } from '../../api/meta';
import { privacyText, type PrivacySection } from '../../i18n/privacy';
import { LanguagePicker } from '../../components/LanguagePicker';

/**
 * The privacy page (RGPD Essentials, V0.31 plan §1.5a).
 *
 * The same shape as the help guide — a full-screen surface of authored prose, card-styled,
 * opened by a handle and closed by Escape — with two properties of its own:
 *
 * PRE-LOGIN IS A FIRST-CLASS CALLER. The page is reachable from the sign-in card, because a
 * person deciding whether to sign in is exactly who a privacy page is for. That works without
 * special cases: `boot()` fetches `/api/capabilities` BEFORE the session check, so `S.caps`
 * is populated (or has honestly failed to null) by the time anything can open this. The host
 * sits at a HIGHER z-index than #loginmodal for the same reason.
 *
 * THE PROCESSOR CARD IS DEPLOYMENT-AWARE, WITH AN HONEST THIRD STATE. Connected installs name
 * Anthropic and Voyage; air-gapped installs state that nothing leaves the server; and a null
 * `caps` (old server, failed fetch) gets wording that admits it does not know — the page must
 * NEVER claim air-gapped on the strength of a fetch that failed (`i18n/privacy.ts` has the
 * full reasoning). `isConnectedDeployment` is the same predicate the About panel uses, so the
 * two surfaces cannot disagree about which deployment this is.
 */

// `readerFeedback` sits with the other "what is stored" cards, and is DRAWN UNCONDITIONALLY
// (V0.46). The panel cannot know whether this deployment runs the widget or whether an admin
// has switched feedback on for an application — that is per scope, and `/api/capabilities` says
// nothing about it — so the prose is conditional instead ("where an administrator has switched
// it on"). The same discipline as the unknown-processor card: say what is true and what it
// depends on, rather than guess.
// `backups` is drawn unconditionally, like `readerFeedback`: this panel says what the
// installation MAY hold, and it cannot know whether a backup exists — nor should a reader have
// to hold a permission to be told that copies of their account data can be made.
const SECTIONS: PrivacySection[] = ['controller', 'server', 'backups', 'readerFeedback', 'browser', 'notStored'];
const TAIL_SECTIONS: PrivacySection[] = ['cookies', 'erasure'];

export interface PrivacyPanelHandle {
  open: () => void;
  close: () => void;
}

export function PrivacyPanel({ host }: { host: HTMLElement }) {
  const s = useAppState();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    host.classList.toggle('show', open);
    host.setAttribute('role', 'dialog');
    host.setAttribute('aria-label', 'Privacy');
  }, [open, host]);

  useEffect(() => {
    const handle: PrivacyPanelHandle = { open: () => setOpen(true), close: () => setOpen(false) };
    registerHandle('privacy', handle);
  }, []);

  // Escape closes it, like every other full-screen surface in the app.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  // Which processor story is true HERE — and "don't know" said out loud rather than guessed.
  const processors: PrivacySection =
    s.caps === null
      ? 'processorsUnknown'
      : isConnectedDeployment(s.caps)
        ? 'processorsConnected'
        : 'processorsOffline';

  const cards: PrivacySection[] = [...SECTIONS, processors, ...TAIL_SECTIONS];

  return (
    <>
      <div className="perm-head">
        <h2>
          <svg className="ico">
            <use href="#i-shield" />
          </svg>
          Privacy &amp; data
        </h2>
        <LanguagePicker title="Language" />
        <button className="btn" id="privacy-close" onClick={() => setOpen(false)}>
          <svg className="ico">
            <use href="#i-x" />
          </svg>
          <span>Close</span>
        </button>
      </div>
      <div className="perm-wrap">
        <div className="perm-inner" id="privacy-body">
          {cards.map((key) => (
            <div className="card" key={key}>
              {/* Authored HTML from i18n/privacy.ts — see the note there on why this is safe. */}
              <div dangerouslySetInnerHTML={{ __html: privacyText(key, s.currentLocale) }} />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
